import { forwardRef, useState } from 'react';
import type { Ref } from 'react';

import { onEnter } from '../../../utils';
import Icon, { icons } from '../Icon';

import InputBase from './InputBase';
import type { InputBaseProps, InputRenderProps } from './InputBase';

export type PasswordInputProps = Omit<InputBaseProps, 'children' | 'type'>;

/**
 * A password input field, with the ability to show/hide the password.
 */
const PasswordInput = ({
  label,
  value,
  onChange,
  size = 'medium',
  placeholder,
  isRequired,
  isDisabled,
  onKeyDown,
  testId,
}: PasswordInputProps, ref: Ref<HTMLInputElement>) => {
  const [isDisplayed, setIsDisplayed] = useState<boolean>(false);
  const onShowHide = () => setIsDisplayed(!isDisplayed);
  return (
    <div className="fossa-form__password-input">
      <InputBase
        type={isDisplayed ? 'text' : 'password'}
        label={label}
        value={value}
        onChange={onChange}
        size={size}
        placeholder={placeholder}
        isRequired={isRequired}
        isDisabled={isDisabled}
        onKeyDown={onKeyDown}
        testId={testId}
      >
        {(inputProps: InputRenderProps) => (
          <input
            ref={ref}
            // We want to inject a large number of unknown props here, so spreading is necessary.
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...inputProps}
          />
        )}
      </InputBase>
      <span
        className={`fossa-form__password-input__icon fossa-form__password-input__icon--${size}`}
        aria-label={isDisplayed ? 'Hide password' : 'Show password'}
        role="button"
        onClick={onShowHide}
        onKeyPress={onEnter(onShowHide)}
        tabIndex={0}
      >
        <Icon icon={isDisplayed ? icons.eyeSlash : icons.eye} />
      </span>
    </div>
  );
};

export default forwardRef<HTMLInputElement, PasswordInputProps>(PasswordInput);
